import React from 'react';
import { FormattedNumber } from 'react-intl';
import { useSelector } from 'react-redux';

import { JPY, USD } from '../../constants/currency-codes';
import Global from '../../store/global-selectors';

/**
 * @typedef {object} FormattedCurrencyProps
 * @property {React.ReactNode|React.ReactNode[]|Function} [children=null] - Child elements or render function
 * @property {number} value - The numeric value to format
 * @property {string} [currencyCode] - The currency code (e.g., 'USD', 'JPY')
 * @property {'accounting' | 'currency'} [currencySign='standard'] - The currency sign display style
 * @property {number} [fractionDigits=2] - Number of fraction digits to display
 * @property {string} [wrappingElement] - HTML element type to wrap the formatted currency (e.g., 'span', 'div')
 */

/**
 * Formats a number as currency based on the provided currency code or seller's default currency
 * @param {FormattedCurrencyProps} props - Component props
 * @returns {React.ReactElement} Formatted currency component
 */
const FormattedCurrency = ({
  children = null,
  value,
  currencyCode: currencyCodeProp,
  currencySign = 'standard',
  fractionDigits = 2,
  wrappingElement = undefined,
}) => {
  // Uses || to treat empty strings as missing — empty string is not a valid ISO 4217 currency code
  const defaultCurrency = useSelector(Global.selectSellerDefaultCurrency) || USD;
  const currencyCode = currencyCodeProp || defaultCurrency;
  const overrideFractionDigits = currencyCode === JPY ? 0 : fractionDigits;

  const formattedNumber = (
    <FormattedNumber
      currency={currencyCode}
      currencySign={currencySign}
      maximumFractionDigits={overrideFractionDigits}
      minimumFractionDigits={overrideFractionDigits}
      style='currency'
      value={value}
    >
      {children}
    </FormattedNumber>
  );

  if (wrappingElement) {
    const WrapperElement = wrappingElement;
    return <WrapperElement>{formattedNumber}</WrapperElement>;
  }

  return formattedNumber;
};

export default FormattedCurrency;
